import type { JSResourceLoader, JSResourceOptions, JSResourceReference } from '../../types';
import { generateId } from '../../utils';
import { eventEmitter as em } from '../event-emitter';

import type { Event, Listener, State } from './types';
import { isJSModuleWithDefaultExport } from './utils';

export function JSResource<T>(
	loader: JSResourceLoader<T>,
	opts: JSResourceOptions,
): JSResourceReference<T> {
	const { moduleId, moduleName } = opts;
	const listeners = new Set<Listener<T>>();

	let promise: Promise<T>;
	let state: State<T> = { type: 'init' };

	function createOnLoad(event: Event) {
		return (module: T) => {
			em.emit('jsresource.load.complete', event);

			state = { type: 'module', module };

			for (const listener of listeners) {
				listener.onLoad(module);
			}
		};
	}

	function createOnError(event: Event) {
		return (error: Error) => {
			em.emit('jsresource.load.error', {
				...event,
				error,
			});

			state = { type: 'error', error };

			for (const listener of listeners) {
				listener.onError?.(error);
			}
		};
	}

	return {
		getModuleId() {
			return moduleId!;
		},
		getModuleName() {
			return moduleName;
		},
		getModuleIfRequired() {
			if (state.type === 'error') {
				throw state.error;
			}

			return state.type === 'module' ? state.module : null;
		},
		load() {
			if (!promise) {
				const event = { moduleId: moduleId!, transactionId: generateId() };
				const onError = createOnError(event);
				const onLoad = createOnLoad(event);

				em.emit('jsresource.load.start', event);

				try {
					promise = loader()
						.then((result) => {
							const module = isJSModuleWithDefaultExport(result) ? result.default : result;

							onLoad(module);

							return module;
						})
						.catch((error) => {
							onError(error);
							throw error;
						});
				} catch (error) {
					if (error instanceof Error) {
						onError(error);
					}
					promise = Promise.reject(error);
				}
			}

			return promise;
		},
		onComplete(onLoad, onError) {
			if (state.type === 'module') {
				onLoad(state.module);
				return () => {};
			}

			if (state.type === 'error') {
				onError?.(state.error);
				return () => {};
			}

			const listener = { onError, onLoad };

			listeners.add(listener);

			return () => {
				listeners.delete(listener);
			};
		},
	};
}
